import { penultimate, ResponseType } from 'utils'
import { EMPTY, Observable, of } from 'rxjs'
import { mergeMap, withLatestFrom } from 'rxjs/operators'
import { ofType, StateObservable } from 'redux-observable'
import { EventResponseAction, EventResponseStatus, ServerActionTypes } from 'api'
import { GlobalState } from 'store'
import { NotificationActions, NotificationType } from 'store/notification'
import { Translation } from 'locale'
import { getErrorMessagesByField } from 'model'
import { BTQAction } from 'epics'

export const SUCCESS_GROWL_TIMEOUT = 5000

export function eventResponseEpic(
  action$: Observable<BTQAction>,
  state$: StateObservable<GlobalState>,
  _dependencies: {}
): Observable<BTQAction> {
  return action$.pipe(
    ofType<BTQAction, ServerActionTypes.EVENT_RESPONSE, EventResponseAction>(ServerActionTypes.EVENT_RESPONSE),
    withLatestFrom(state$.pipe(penultimate())),
    mergeMap(([action, state]) => {
      const pendingActions = state.notificationStore.pendingActions
      const pendingAction = pendingActions[action.payload.correlationId]
      const responseMeta = pendingAction && pendingAction.meta && pendingAction.meta.responseMeta
      const targetWindowId = pendingAction && pendingAction.meta && pendingAction.meta.sourceWindowId

      if (action.payload.status === EventResponseStatus.FAILED) {
        const errorMessage = action.payload.messages.join('\r\n')
        const validationErrorMessage = action.payload.validationIssues.map(x => x.errorMessage).join('\r\n')
        if (responseMeta && responseMeta.errorResponse === ResponseType.DIALOG) {
          return of(
            NotificationActions.showDialog({
              type: NotificationType.Error,
              title: Translation.TC_NOTIFICATION_ERROR,
              messageText: responseMeta.errorMessage || errorMessage || validationErrorMessage || 'Unknown error',
              buttons: [Translation.TC_BUTTON_CANCEL],
              targetWindowId: targetWindowId
            })
          )
        } else if (responseMeta && responseMeta.errorResponse === ResponseType.GROWL) {
          return of(
            NotificationActions.showGrowl({
              type: NotificationType.Error,
              messageText: responseMeta.errorMessage || errorMessage || validationErrorMessage || 'Unknown error',
              targetWindowId: targetWindowId
            })
          )
        } else if (responseMeta && responseMeta.errorResponse === ResponseType.NONE) {
          return EMPTY
        } else {
          return of(
            NotificationActions.showGrowl({
              type: NotificationType.Error,
              messageText: errorMessage || validationErrorMessage || 'Unknown error',
              targetWindowId: targetWindowId
            })
          )
        }
      } else if (action.payload.status === EventResponseStatus.VALIDATION_ISSUES) {
        const validationErrorMessage = getErrorMessagesByField(action.payload.validationIssues).join('\r\n')
        if (responseMeta && responseMeta.errorResponse === ResponseType.DIALOG) {
          return of(
            NotificationActions.showDialog({
              type: NotificationType.Error,
              title: Translation.TC_NOTIFICATION_ERROR,
              messageText: responseMeta.errorMessage || validationErrorMessage || 'Unknown validation issue',
              buttons: [Translation.TC_BUTTON_CANCEL],
              targetWindowId: targetWindowId
            })
          )
        } else if (responseMeta && responseMeta.errorResponse === ResponseType.GROWL) {
          return of(
            NotificationActions.showGrowl({
              type: NotificationType.Error,
              messageText: responseMeta.errorMessage || validationErrorMessage || 'Unknown validation issue',
              targetWindowId: targetWindowId
            })
          )
        } else if (responseMeta && responseMeta.errorResponse === ResponseType.NONE) {
          return EMPTY
        } else {
          return of(
            NotificationActions.showGrowl({
              type: NotificationType.Error,
              messageText: validationErrorMessage || 'Unknown validation issue',
              targetWindowId: targetWindowId
            })
          )
        }
      } else if (action.payload.status === EventResponseStatus.SUCCEEDED) {
        const successMessage = action.payload.messages.join('\r\n')
        if (responseMeta && responseMeta.successResponse === ResponseType.DIALOG) {
          return of(
            NotificationActions.showDialog({
              type: NotificationType.Success,
              title: Translation.TC_NOTIFICATION_SUCCESS,
              messageText: responseMeta.successMessage || successMessage,
              targetWindowId: targetWindowId,
              buttons: [Translation.TC_BUTTON_OK]
            })
          )
        } else if (responseMeta && responseMeta.successResponse === ResponseType.GROWL) {
          return of(
            NotificationActions.showGrowl({
              type: NotificationType.Success,
              messageText: responseMeta.successMessage || successMessage,
              targetWindowId: targetWindowId,
              timeout: SUCCESS_GROWL_TIMEOUT
            })
          )
        } else {
          return EMPTY
        }
      } else {
        throw new Error(`Unhandled EventResponseStatus [${action.payload.status}]`)
      }
    })
  )
}
